import React from 'react';
import ComputingState from '../../../../../shared/components/common/computing-state/ComputingState';
import './BrandsTable.scss';
import CustomTooltip from '../../../../../shared/components/common/tooltip/CustomTooltip';
import { t } from 'i18next';
import { DataTable } from '../../../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import EmptyPanel from '../../../../../shared/components/empty-panel/EmptyPanel';
import { AllBrandsStatsResponse } from '../../../../../shared/models/projects/stats/AllBrandsStatsResponse';
import { CellBox } from './components/cell-box/CellBox';
import { KeywordResponse } from '../../../../../shared/models/monitor/KeywordResponse';
import { TagsResponse } from '../../../../../shared/models/tags/TagsResponse';
import { findTagIdInKeyword } from '../../../../../shared/helpers/keyword/KeywordHelper';
import { getTagLabel } from '../../../../../shared/helpers/tags/TagsHelper';
import { Button } from 'primereact/button';
import useExport from '../../../../../shared/hooks/global/UseExport';
import BrandIcon from '../../../../../shared/components/brand-icon/BrandIcon';
import { normalizeKeyword } from '../../../../../shared/utils/utils';
import BrandBadges from '../../../../../shared/components/brand-badges/BrandBadges';

interface BrandsTableProps {
  allBrandsStatsResponse: AllBrandsStatsResponse[];
  keywordsMapper: Map<string, string>;
  commonKeywords: KeywordResponse[];
  tags: TagsResponse[];
  // V2 / NEW_STATS: replace the Brand Score column with the new Visibility % metric
  // (responses mentioning the brand / total responses). When false (V1), shows Brand Score.
  showVisibility?: boolean;
  // V2 / NEW_STATS: replace the Median Position column with Average Position. When false (V1), shows Median.
  showAveragePosition?: boolean;
  // Optional i18n overrides for the panel header (defaults keep the project-page labels).
  titleKey?: string;
  titleTooltipKey?: string;
  // Optional table overrides (defaults keep the project-page behaviour): the prompt-view V2
  // "Top brands" panel shows a fixed, unpaginated list sorted the same way as its chart.
  paginator?: boolean;
  rows?: number;
  sortField?: string;
  // Narrow-panel mode (prompt-view V2 side column): drops the Brand Score column, shrinks the
  // numeric columns and disables horizontal scrolling so all columns fit a ~360px panel.
  compact?: boolean;
  // Hides the CSV export button (prompt-view V2 side panel).
  showExport?: boolean;
  // Drops the Brand Score column (and its CSV column) even when showVisibility is on. Used by the
  // question stats page, which sources stats from monitor_keyword_stats and has no brand score yet.
  hideBrandScore?: boolean;
  onRowMouseEnter?: (brandLabel: string) => void;
  onRowMouseLeave?: (brandLabel: string) => void;
  brandColors?: { name: string; color: string }[];
  // Overrides the "#" column: maps normalizeKeyword(keyword).toLowerCase() -> the brand's real rank
  // in the full ranking. Lets the tracked brand keep its true position when it is appended below the
  // top 7. Falls back to the row index when absent (project-page tables pass nothing).
  rankByKeyword?: Map<string, number>;
  // Renders an in-place row skeleton (keeping the panel title) instead of the table while the stats
  // load — so the section owns its own loading state, like the project page components.
  isLoading?: boolean;
  // Marki w trakcie przeliczania statystyk — zamiast tabeli ten sam stan „liczymy” co przy nowym projekcie.
  recomputingBrands?: string[];
  // Brand keys (the stats row's `keyword`) that get the „Main brand” / „Selected brand” badges.
  mainBrandKey?: string;
  selectedBrandKey?: string;
}

export const BrandsTable: React.FC<BrandsTableProps> = ({
  allBrandsStatsResponse,
  keywordsMapper,
  commonKeywords,
  tags,
  showVisibility = false,
  showAveragePosition = false,
  titleKey = 'brandsRanking',
  titleTooltipKey = 'brandsRankingTooltip',
  paginator = true,
  rows = 5,
  sortField = 'shareOfVoice',
  compact = false,
  showExport = true,
  hideBrandScore = false,
  onRowMouseEnter,
  onRowMouseLeave,
  brandColors,
  rankByKeyword,
  isLoading = false,
  recomputingBrands = [],
  mainBrandKey,
  selectedBrandKey,
}) => {
  const numericColumnWidth = compact ? '70px' : '95px';
  const { canExport } = useExport();

  // Loading renders the REAL table (headers, column widths, frame) with a shimmer bar in each cell, so
  // the skeleton mirrors the loaded layout. Placeholder rows carry only a fake keyword key.
  const skeletonRows = Array.from(
    { length: rows },
    (_, i) => ({ keyword: `__skeleton_${i}` } as AllBrandsStatsResponse)
  );
  const skeletonCell = (
    <div
      className="project-skeleton__bar"
      style={{ width: '70%', height: 18, borderRadius: 6, margin: '0 auto' }}
    />
  );

  // Compact (Top 7 side panel) headers explain their metric on hover. targetRef ids are prefixed
  // with titleKey so a second BrandsTable on the same page can't collide.
  const compactHeader = (label: string, tooltipKey: string, idSuffix: string) => {
    const headerId = `${titleKey}-${idSuffix}-header`;
    return (
      <>
        <span id={headerId}>{label}</span>
        <CustomTooltip tooltipText={tooltipKey} targetRef={headerId} />
      </>
    );
  };

  // No client-side 0-brand-score filter: orphan brands (no data under the account's current models)
  // are already excluded server-side in getAllBrandsStats, and live-but-low brands are intentionally
  // shown. So the table/export render whatever the backend returns.

  const resolveKeyword = (keyword: string): string => {
    const tagId = commonKeywords ? findTagIdInKeyword(keyword, commonKeywords) : null;
    const keywordFromTags = tags ? getTagLabel(tags, tagId, keyword) : keyword;
    return keywordsMapper.get(keywordFromTags) || keywordFromTags;
  };

  const handleExport = () => {
    if (!canExport || allBrandsStatsResponse.length === 0) return;

    const headers = [
      t('brand'),
      showAveragePosition ? t('averagePosition') : t('medianPosition'),
      // V2 shows Brand Score as its own column, between position and Visibility.
      ...(showVisibility && !hideBrandScore ? [t('brandScore')] : []),
      showVisibility ? t('visibility') : t('brandScore'),
      t('shareOfVoice'),
    ];
    const rows = allBrandsStatsResponse.map((row) => [
      `"${resolveKeyword(row.keyword).replace(/"/g, '""')}"`,
      showAveragePosition ? row.position : row.medianPosition,
      ...(showVisibility && !hideBrandScore ? [row.brandScore] : []),
      showVisibility ? row.visibility ?? 0 : row.brandScore,
      row.shareOfVoice,
    ]);

    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'brands-ranking.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="brands-table stats__panel">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t(titleKey)}
          <CustomTooltip tooltipText={titleTooltipKey} addMargin={false} />
        </div>
        {/* Przy pustej tabeli nie ma czego eksportować. */}
        {showExport && allBrandsStatsResponse.length > 0 && (
          <>
            <Button
              id="btn-export-brands-ranking"
              className={`action-btn ${!canExport ? 'btn--disabled-tooltip' : ''}`}
              onClick={handleExport}
            >
              <span className={`ico--download ${!canExport ? 'ico--disabled' : ''} ico--xs`} />
            </Button>
            <CustomTooltip
              tooltipText={!canExport ? 'upgradePlanTrialExportMsg' : 'exportCsv'}
              targetRef="btn-export-brands-ranking"
            />
          </>
        )}
      </div>
      {recomputingBrands.length > 0 ? (
        <div className="stats-box__content brands-table__recomputing">
          <ComputingState
            title="statsRecomputingTitle"
            subtitle="statsRecomputingSubtitle"
            subtitleParams={{ brands: recomputingBrands.join(', ') }}
          />
        </div>
      ) : (
        <DataTable
          stripedRows
          paginator={!isLoading && paginator}
          scrollable={!compact}
          paginatorClassName="pagination"
          removableSort
          rows={rows}
          value={isLoading ? skeletonRows : allBrandsStatsResponse}
          emptyMessage={() => (isLoading ? null : <EmptyPanel />)}
          tableClassName="table"
          className="table-wrapper table-wrapper--stack"
          responsiveLayout="stack"
          rowsPerPageOptions={[5, 10, 15]}
          currentPageReportTemplate={t('rowsNumber') + ':'}
          sortField={sortField}
          sortOrder={-1}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          onRowMouseEnter={
            onRowMouseEnter
              ? (e) => onRowMouseEnter(resolveKeyword((e.data as AllBrandsStatsResponse).keyword))
              : undefined
          }
          onRowMouseLeave={
            onRowMouseLeave
              ? (e) => onRowMouseLeave(resolveKeyword((e.data as AllBrandsStatsResponse).keyword))
              : undefined
          }
        >
          <Column
            header="#"
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center table__cell--stack-rank"
            body={(rowData: AllBrandsStatsResponse, { rowIndex }) =>
              isLoading
                ? skeletonCell
                : `#${rankByKeyword?.get(normalizeKeyword(rowData.keyword).toLowerCase()) ?? rowIndex + 1}`
            }
            style={{ width: '30px' }}
          />
          <Column
            sortable
            field="keyword"
            bodyClassName="table__cell--stack-title"
            header={t('brand')}
            headerClassName="table__header"
            body={(rowData) => {
              if (isLoading) return skeletonCell;
              const label = resolveKeyword(rowData.keyword);
              const bc = brandColors?.find((c) => c.name.toLowerCase() === label.toLowerCase());
              const displayLabel = bc?.name ?? label;
              const badges = (
                <BrandBadges
                  isMain={!!mainBrandKey && rowData.keyword === mainBrandKey}
                  isSelected={!!selectedBrandKey && rowData.keyword === selectedBrandKey}
                />
              );
              return bc ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <BrandIcon name={displayLabel} color={bc.color} size="xs" />
                  <span className="brands-table__label capitalize">{displayLabel}</span>
                  {badges}
                </span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="capitalize">{label}</span>
                  {badges}
                </span>
              );
            }}
          />
          <Column
            sortable
            field={showAveragePosition ? 'position' : 'medianPosition'}
            // Compact (narrow side panel): the short "Position" header keeps the column at 70px.
            header={
              compact
                ? compactHeader(t('position'), 'positionColumnTooltip', 'position')
                : showAveragePosition
                ? t('averagePosition')
                : t('medianPosition')
            }
            headerClassName="table__header table__header--center"
            bodyClassName={`table__cell--center`}
            style={{ width: numericColumnWidth }}
            body={(rowData: AllBrandsStatsResponse, column) =>
              isLoading ? (
                skeletonCell
              ) : (
                <CellBox
                  value={showAveragePosition ? rowData.position : rowData.medianPosition}
                  trend={showAveragePosition ? rowData.trends.positionTrend : rowData.trends.medianTrend}
                  suffix=""
                  tooltipId={`median-${column.rowIndex}`}
                />
              )
            }
          />
          {showVisibility && !compact && !hideBrandScore && (
            <Column
              sortable
              field="brandScore"
              header={t('brandScore')}
              headerClassName="table__header table__header--center"
              bodyClassName={`table__cell--center`}
              style={{ width: numericColumnWidth }}
              body={(rowData: AllBrandsStatsResponse, column) =>
                isLoading ? (
                  skeletonCell
                ) : (
                  <CellBox
                    value={rowData.brandScore}
                    trend={rowData.trends.visibilityTrend}
                    suffix="%"
                    tooltipId={`brandScore-${column.rowIndex}`}
                  />
                )
              }
            />
          )}
          <Column
            sortable
            field={showVisibility ? 'visibility' : 'brandScore'}
            header={
              compact && showVisibility
                ? compactHeader(t('visibility'), 'visibilityColumnTooltip', 'visibility')
                : showVisibility
                ? t('visibility')
                : t('brandScore')
            }
            headerClassName="table__header table__header--center"
            bodyClassName={`table__cell--center`}
            style={{ width: numericColumnWidth }}
            body={(rowData: AllBrandsStatsResponse, column) =>
              isLoading ? (
                skeletonCell
              ) : showVisibility ? (
                <CellBox
                  value={rowData.visibility ?? 0}
                  trend={rowData.visibilityTrend ?? 0}
                  suffix="%"
                  tooltipId={`visibility-${column.rowIndex}`}
                />
              ) : (
                <CellBox
                  value={rowData.brandScore}
                  trend={rowData.trends.visibilityTrend}
                  suffix="%"
                  tooltipId={`visibility-${column.rowIndex}`}
                />
              )
            }
          />
          <Column
            sortable
            field="shareOfVoice"
            // Compact (narrow side panel): "Share of Voice" wraps to two lines in the 70px column and
            // inflates the whole header row — abbreviate; the tooltip spells out the full name.
            header={
              compact ? compactHeader(t('sovShort'), 'sovColumnTooltip', 'sov') : t('shareOfVoice')
            }
            headerClassName="table__header table__header--center"
            bodyClassName={`table__cell--center`}
            style={{ width: numericColumnWidth }}
            body={(rowData) =>
              isLoading ? skeletonCell : `${Number(rowData.shareOfVoice).toFixed(1)}%`
            }
          />
        </DataTable>
      )}
    </div>
  );
};
